import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subscription } from 'rxjs';
import {
  ApplyCreatedOrdersDefaults,
  CreateOrderInput,
  CreateOrderItemInput,
  CreateOrderResult,
  formatOrderCommissionLabel,
  formatScheduledPaymentDateDisplay,
  formatUserMessageText,
  logOrderDevDetail,
  OrderService,
  RejectedService,
  resolveCreateOrderRejectionMessage,
  resolveOrderGraphqlUserMessage,
  resolveCreateOrderWarningMessage,
  toCreateOrderResult,
  toScheduledPaymentDateIso,
  UserMessage,
  OrderWarning,
} from 'src/app/entities/order';

export interface CreateOrderDialogData {
  /** Compat: se mapea a items sin ticketIds. */
  serviceIds?: string[];
  /** Preferido cuando hay ticketIds (Ticketería). */
  items?: CreateOrderItemInput[];
  contextLabel?: string;
  /**
   * Fechas de operación de los servicios elegidos. Sirven para mostrar
   * el plazo para confirmar ya calculado (45 días antes de la más cercana).
   */
  operationDates?: Array<string | null | undefined>;
}

/** Días antes de la fecha del servicio para el plazo para confirmar. */
const FREEZE_DAYS_BEFORE_OPERATION = 45;

/** Acepta ISO, YYYY-MM-DD y DD/MM/YYYY (mismos formatos que el backend). */
function parseOperationDate(value: string | null | undefined): Date | null {
  if (!value?.trim()) {
    return null;
  }
  const trimmed = value.trim();
  const dmy = /^(\d{1,2})[\/.](\d{1,2})[\/.](\d{4})$/.exec(trimmed);
  if (dmy) {
    const date = new Date(
      Number(dmy[3]),
      Number(dmy[2]) - 1,
      Number(dmy[1])
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Plazo para confirmar por defecto: 45 días antes de la fecha más
 * cercana, pero nunca una fecha que ya pasó: si ese día ya pasó,
 * el plazo es hoy.
 */
function defaultFreezeDeadline(
  operationDates: Array<string | null | undefined> | undefined
): Date | null {
  const dates = (operationDates ?? [])
    .map(parseOperationDate)
    .filter((date): date is Date => date !== null);
  if (!dates.length) {
    return null;
  }
  const earliest = new Date(Math.min(...dates.map((date) => date.getTime())));
  earliest.setDate(earliest.getDate() - FREEZE_DAYS_BEFORE_OPERATION);
  earliest.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return earliest.getTime() < today.getTime() ? today : earliest;
}

export interface CreateOrderDialogResult {
  success: boolean;
  result: CreateOrderResult | null;
  submittedInput?: ApplyCreatedOrdersDefaults | null;
  shouldRefresh?: boolean;
}

type ViewState = 'form' | 'loading' | 'result' | 'error';

@Component({
  selector: 'app-create-order',
  standalone: true,
  templateUrl: './create-order.component.html',
  styleUrls: ['./create-order.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    TablerIconsModule,
  ],
  host: { class: 'create-order-host' },
})
export class CreateOrderComponent implements OnInit, OnDestroy {
  state: ViewState = 'form';
  form: FormGroup;

  items: CreateOrderItemInput[] = [];
  contextLabel: string | null = null;

  usuario = '';
  email = '';

  result: CreateOrderResult | null = null;
  submittedInput: ApplyCreatedOrdersDefaults | null = null;
  errorTitle: string | null = null;
  errorMessage: string | null = null;

  private lastInput: CreateOrderInput | null = null;
  private createSub: Subscription | null = null;
  private commissionSub: Subscription | null = null;
  commissionValueTouched = false;
  confirmingOrderId: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<CreateOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateOrderDialogData,
    private readonly formBuilder: FormBuilder,
    private readonly orderService: OrderService
  ) {
    this.form = this.formBuilder.group({
      scheduledPaymentDate: [null],
      freezeDeadlineAt: [null, Validators.required],
      asOfficial: [false],
      reservationNumber: [null],
      orderObservation: [null],
      commissionEnabled: [false],
      commissionType: ['PERCENTAGE'],
      commissionValue: [null]
    });
  }

  ngOnInit(): void {
    this.items = this.resolveItems(this.data);
    this.contextLabel = this.data?.contextLabel ?? null;
    const freezeDeadline = defaultFreezeDeadline(this.data?.operationDates);
    if (freezeDeadline) {
      this.form.patchValue({ freezeDeadlineAt: freezeDeadline });
    }
    const commissionSub = new Subscription();
    const enabledChanges = this.form
      .get('commissionEnabled')
      ?.valueChanges.subscribe((enabled) => {
        if (
          enabled &&
          this.form.value.commissionType === 'PERCENTAGE' &&
          this.form.value.commissionValue == null
        ) {
          this.form.patchValue({ commissionValue: 2.91 });
        }
      });
    const typeChanges = this.form
      .get('commissionType')
      ?.valueChanges.subscribe((type) => {
        const value = this.form.value.commissionValue;
        if (type === 'FIXED' && this.isDefaultPercent(value)) {
          this.form.patchValue({ commissionValue: null });
          return;
        }
        if (
          type === 'PERCENTAGE' &&
          this.form.value.commissionEnabled &&
          value == null
        ) {
          this.form.patchValue({ commissionValue: 2.91 });
        }
      });
    if (enabledChanges) {
      commissionSub.add(enabledChanges);
    }
    if (typeChanges) {
      commissionSub.add(typeChanges);
    }
    this.commissionSub = commissionSub;

    // El plazo es obligatorio, salvo que la OS nazca confirmada.
    const officialChanges = this.form
      .get('asOfficial')
      ?.valueChanges.subscribe((official) => {
        const control = this.form.get('freezeDeadlineAt');
        if (!control) {
          return;
        }
        if (official) {
          control.clearValidators();
        } else {
          control.setValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
    if (officialChanges) {
      commissionSub.add(officialChanges);
    }

    const nombre = localStorage.getItem('nombre') ?? '';
    const apellido = localStorage.getItem('apellido') ?? '';
    this.usuario = `${nombre} ${apellido}`.trim();
    this.email = localStorage.getItem('email') ?? '';
  }

  ngOnDestroy(): void {
    this.createSub?.unsubscribe();
    this.commissionSub?.unsubscribe();
  }

  /** 2.91 es el porcentaje por defecto, no un monto fijo. */
  private isDefaultPercent(value: number | null | undefined): boolean {
    return value != null && Math.round(Number(value) * 100) === 291;
  }

  /** Compat UI: cantidad de items (padres / selecciones). */
  get serviceIds(): string[] {
    return this.items.map((item) => item.serviceId);
  }

  get ticketSelectionCount(): number {
    return this.items.reduce(
      (acc, item) => acc + (item.ticketIds?.length ?? 0),
      0
    );
  }

  get canSubmit(): boolean {
    return (
      this.state === 'form' &&
      this.items.length > 0 &&
      (this.form.value.asOfficial === true ||
        (this.form.get('freezeDeadlineAt')?.valid ?? false))
    );
  }

  get showCommissionError(): boolean {
    return this.commissionValueTouched && Boolean(this.commissionError);
  }

  markCommissionTouched(): void {
    this.commissionValueTouched = true;
  }

  get commissionError(): string | null {
    if (!this.form.value.commissionEnabled) {
      return null;
    }
    const value = Number(this.form.value.commissionValue);
    if (!Number.isFinite(value) || value <= 0) {
      return 'Ingresa un valor mayor a 0.';
    }
    if (this.form.value.commissionType === 'PERCENTAGE' && value > 100) {
      return 'El porcentaje no puede ser mayor a 100.';
    }
    return null;
  }

  private resolveItems(data: CreateOrderDialogData | null | undefined): CreateOrderItemInput[] {
    if (data?.items?.length) {
      return data.items
        .filter((item) => !!item?.serviceId)
        .map((item) => {
          const ticketIds = (item.ticketIds || [])
            .map((id) => String(id || '').trim())
            .filter(Boolean);
          return ticketIds.length
            ? { serviceId: String(item.serviceId), ticketIds }
            : { serviceId: String(item.serviceId) };
        });
    }

    const unique = [...new Set((data?.serviceIds ?? []).filter((id) => !!id))];
    return unique.map((serviceId) => ({ serviceId: String(serviceId) }));
  }

  get isBusy(): boolean {
    return this.state === 'loading';
  }

  confirmar(os: { orderId: string }): void {
    if (!os.orderId || this.confirmingOrderId) {
      return;
    }
    this.confirmingOrderId = os.orderId;
    this.orderService.confirmPreliminaryOrder(os.orderId).subscribe({
      next: (confirmed) => {
        this.confirmingOrderId = null;
        if (!this.result || !confirmed) {
          return;
        }
        this.result = {
          ...this.result,
          createdOrders: this.result.createdOrders.map((item) =>
            item.orderId === os.orderId
              ? {
                  ...item,
                  orderStatus: confirmed.orderStatus,
                  displayCode: confirmed.displayCode || item.displayCode,
                  pdfStatus: confirmed.pdfStatus
                }
              : item
          )
        };
      },
      error: () => {
        this.confirmingOrderId = null;
      }
    });
  }

  crearOS(): void {
    if (this.state === 'form' && this.form.value.commissionEnabled && this.commissionError) {
      this.commissionValueTouched = true;
      return;
    }

    if (!this.canSubmit && this.state !== 'error') {
      return;
    }

    if (this.state === 'error' && this.lastInput) {
      this.startCreate(this.lastInput);
      return;
    }

    if (!this.canSubmit) {
      return;
    }

    const fecha = this.form.value.scheduledPaymentDate as Date | null;
    const corte = this.form.value.freezeDeadlineAt as Date | null;
    const asOfficial = this.form.value.asOfficial === true;
    const commission = this.form.value.commissionEnabled
      ? {
          type: this.form.value.commissionType as 'PERCENTAGE' | 'FIXED',
          value: Number(this.form.value.commissionValue)
        }
      : null;
    const input: CreateOrderInput = {
      items: this.items.map((item) =>
        item.ticketIds?.length
          ? { serviceId: item.serviceId, ticketIds: item.ticketIds }
          : { serviceId: item.serviceId }
      ),
      createdBy: { name: this.usuario, email: this.email },
      scheduledPaymentDate: fecha ? toScheduledPaymentDateIso(fecha) : null,
      freezeDeadlineAt:
        !asOfficial && corte ? toScheduledPaymentDateIso(corte) : null,
      asOfficial,
      reservationNumber: this.form.value.reservationNumber?.trim() || null,
      orderObservation: this.form.value.orderObservation?.trim() || null,
      commission
    };

    this.submittedInput = {
      reservationNumber: input.reservationNumber,
      orderObservation: input.orderObservation,
      scheduledPaymentDate: input.scheduledPaymentDate,
      commission: input.commission ?? null
    };

    this.startCreate(input);
  }

  private startCreate(input: CreateOrderInput): void {
    this.lastInput = input;
    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.result = null;
    this.dialogRef.disableClose = true;

    this.createSub?.unsubscribe();
    this.createSub = this.orderService.createOrderWithPolling(input).subscribe({
      next: (job) => {
        this.dialogRef.disableClose = false;
        const result = toCreateOrderResult(job);
        this.result = result;
        this.dialogRef.disableClose = true;
        this.state = 'result';
      },
      error: (error) => {
        this.dialogRef.disableClose = true;
        logOrderDevDetail('createOrder.error', error);
        const msg = resolveOrderGraphqlUserMessage(
          error,
          'No se pudo crear las órdenes. Reintenta o contacta a Soporte TI.'
        );
        this.errorTitle = msg.title;
        this.errorMessage = formatUserMessageText(msg);
        this.state = 'error';
      }
    });
  }

  get createdOrders() {
    return this.result?.createdOrders ?? [];
  }

  get payRows() {
    return this.createdOrders
      .map((order) => this.payRowForOrder(order))
      .filter((row): row is NonNullable<typeof row> => row != null);
  }

  private payRowForOrder(order: {
    orderId: string;
    orderNumber?: string | number | null;
    displayCode?: string | null;
    orderStatus?: string | null;
    baseAmount?: number | null;
    commissionPercent?: number | null;
    commissionAmount?: number | null;
    total?: number | null;
  }) {
    const orderNumber = String(order.orderNumber ?? '');
    const displayCode =
      order.displayCode ||
      (order.orderStatus === 'PRELIMINAR'
        ? `P-${orderNumber}`
        : orderNumber);
    const apiAmount = Number(order.commissionAmount);
    const baseFromApi =
      order.baseAmount != null && Number.isFinite(Number(order.baseAmount))
        ? Number(order.baseAmount)
        : null;
    const totalFromApi =
      order.total != null && Number.isFinite(Number(order.total))
        ? Number(order.total)
        : null;

    if (order.commissionAmount != null && Number.isFinite(apiAmount)) {
      const base =
        baseFromApi ??
        (totalFromApi != null ? this.roundMoney(totalFromApi - apiAmount) : null);
      if (base == null) {
        return null;
      }
      return {
        orderId: order.orderId,
        orderNumber,
        displayCode,
        baseAmount: base,
        commissionPercent:
          order.commissionPercent != null &&
          Number.isFinite(Number(order.commissionPercent))
            ? Number(order.commissionPercent)
            : null,
        commissionAmount: apiAmount,
        total: totalFromApi ?? this.roundMoney(base + apiAmount)
      };
    }

    if (
      baseFromApi != null &&
      totalFromApi != null &&
      totalFromApi > baseFromApi
    ) {
      const amount = this.roundMoney(totalFromApi - baseFromApi);
      return {
        orderId: order.orderId,
        orderNumber,
        displayCode,
        baseAmount: baseFromApi,
        commissionPercent:
          baseFromApi > 0 ? this.roundMoney((amount / baseFromApi) * 100) : null,
        commissionAmount: amount,
        total: totalFromApi
      };
    }

    const submitted = this.lastInput?.commission;
    const base = baseFromApi ?? totalFromApi;
    if (base == null) {
      return null;
    }
    const value = Number(submitted?.value);
    if (submitted && Number.isFinite(value) && value > 0) {
      const amount =
        submitted.type === 'PERCENTAGE'
          ? this.roundMoney((base * value) / 100)
          : this.roundMoney(value);
      const percent =
        submitted.type === 'PERCENTAGE'
          ? value
          : base > 0
            ? this.roundMoney((amount / base) * 100)
            : null;
      return {
        orderId: order.orderId,
        orderNumber,
        displayCode,
        baseAmount: base,
        commissionPercent: percent,
        commissionAmount: amount,
        total: this.roundMoney(base + amount)
      };
    }

    return {
      orderId: order.orderId,
      orderNumber,
      displayCode,
      baseAmount: base,
      commissionPercent: null,
      commissionAmount: null,
      total: base
    };
  }

  private roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
  }

  commissionLabel(order: {
    commissionPercent?: number | null;
    commissionAmount?: number | null;
    baseAmount?: number | null;
  }): string {
    return formatOrderCommissionLabel(order);
  }

  get rejectedServices() {
    return this.result?.rejectedServices ?? [];
  }

  get warnings() {
    return this.result?.warnings ?? [];
  }

  /** Warnings únicos por reason (+ detalle de servicios afectados). */
  get warningCards(): Array<{
    reason: string;
    title: string;
    body: string;
    serviceIds: string[];
  }> {
    const byReason = new Map<
      string,
      { reason: string; title: string; body: string; serviceIds: string[] }
    >();

    for (const warning of this.warnings) {
      const reason = warning.reason || 'UNKNOWN';
      const msg = this.warningMessage(warning);
      const existing = byReason.get(reason);
      const serviceId = warning.serviceId?.trim();

      if (existing) {
        if (serviceId && !existing.serviceIds.includes(serviceId)) {
          existing.serviceIds.push(serviceId);
        }
        continue;
      }

      byReason.set(reason, {
        reason,
        title: msg.title,
        body: this.messageText(msg),
        serviceIds: serviceId ? [serviceId] : []
      });
    }

    return [...byReason.values()];
  }

  get hasCreated(): boolean {
    return this.createdOrders.length > 0;
  }

  get hasWarnings(): boolean {
    return this.warnings.length > 0;
  }

  get hasRejections(): boolean {
    return this.rejectedServices.length > 0;
  }

  /** Solo rechazos/error dejan la grilla desfasada → reload del padre. */
  get needsDataRefresh(): boolean {
    return this.state === 'error' || this.hasRejections;
  }

  /** Rechazos por ítem (incluye ticketId cuando aplica). */
  get rejectionCards(): Array<{
    reason: string;
    title: string;
    body: string;
    serviceId: string | null;
    ticketId: string | null;
    message: string | null;
  }> {
    return this.rejectedServices.map((rejected) => {
      const msg = this.rejectionMessage(rejected);
      return {
        reason: rejected.reason || 'UNKNOWN',
        title: msg.title,
        body: this.messageText(msg),
        serviceId: rejected.serviceId?.trim() || null,
        ticketId: rejected.ticketId?.trim() || null,
        message: rejected.message?.trim() || null
      };
    });
  }

  getReservationNumber(os: {
    reservationNumber?: string | null;
  }): string | null {
    return os.reservationNumber ?? this.submittedInput?.reservationNumber ?? null;
  }

  freezeLabel(os: { freezeDeadlineAt?: string | null }): string | null {
    const label = formatScheduledPaymentDateDisplay(os.freezeDeadlineAt);
    return label && label !== '-' ? label : null;
  }

  rejectionMessage(rejected: RejectedService): UserMessage {
    return resolveCreateOrderRejectionMessage(rejected);
  }

  warningMessage(warning: OrderWarning): UserMessage {
    return resolveCreateOrderWarningMessage(warning);
  }

  messageText(message: UserMessage): string {
    return formatUserMessageText(message);
  }

  cerrar(success = this.hasCreated): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close({
      success,
      result: this.result,
      submittedInput: this.submittedInput,
      shouldRefresh: this.needsDataRefresh
    } as CreateOrderDialogResult);
  }

  /** Cierra tras error/rechazos y pide reload al padre. */
  cerrarYActualizar(): void {
    this.cerrar(false);
  }

  cancelar(): void {
    if (this.isBusy) {
      return;
    }

    this.dialogRef.disableClose = false;
    this.dialogRef.close({
      success: false,
      result: null,
      submittedInput: null,
      shouldRefresh: this.needsDataRefresh
    } as CreateOrderDialogResult);
  }

  backToForm(): void {
    this.dialogRef.disableClose = false;
    this.state = 'form';
    this.errorTitle = null;
    this.errorMessage = null;
  }
}
